<!--
// app/components/Shared/standings/TableStandingHome.vue
-->
<script setup lang="ts">
import type { Phase } from "~/src/season-cluster/domain/models/02.Phase";
import type {
  StandingPlayoffColors,
  StandingPositionColor,
} from "~/src/season-cluster/domain/models/01.Season";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useRegions } from "~/app/composables/regions";
import { setAliasUrl, slugify } from "~/src/Shared/utils";
import { useWebContext } from "~/app/composables/webContext";
import StandingsLegend from "~/app/components/Shared/standings/StandingsLegend.vue";
import seasonService from "~/src/season-cluster/domain/services/seasonService";

const localePath = useLocalePath();
const { findSeasonStandingPhase, getPositionLegendForGroup } = seasonService();
const { regionParam } = useRegions();
const { currentSeason } = useCompetitionStore();
const { contextSiteTitle, webContextGenders } = useWebContext();

const standingPhase = ref<Phase>();
const positionLegend = ref<StandingPlayoffColors[] | StandingPlayoffColors[][]>();
const secondaryPositionLegend = ref<StandingPlayoffColors[] | StandingPlayoffColors[][]>();

function flattenPositionLegend(
  legend: StandingPlayoffColors[] | StandingPlayoffColors[][] | undefined
): StandingPlayoffColors[] | undefined {
  if (!legend) return undefined;
  if (Array.isArray(legend[0])) {
    return (legend as StandingPlayoffColors[][]).flat();
  }
  return legend as StandingPlayoffColors[];
}

function getRowPositionColorTailwindClass(playoffColor: StandingPositionColor | undefined) {
  //This function MUST be defined in the Vue file. In any other cases, Tailwind won't render the classes.
  switch (playoffColor) {
    case "green":
      return "before:!bg-[#10E217]";
    case "orange":
      return "before:!bg-amber-400";
    case "softOrange":
      return "before:!bg-[#EC7B39]";
    case "darkOrange":
      return "before:!bg-[#FD5900]";
    case "red":
      return "before:!bg-red-500";
    case "yellow":
      return "before:!bg-[#FFFB00]";
    case "brown":
      return "before:!bg-[#A8834E]";
    case "gray":
      return "before:!bg-gray-500";
    case "greenDashed":
      return "before:!bg-[repeating-linear-gradient(to_bottom,#10E217_0,#10E217_8px,_transparent_8px,_transparent_12px)]";
    case "yellowDashed":
      return "before:!bg-[repeating-linear-gradient(to_bottom,#FFFB00_0,#FFFB00_8px,_transparent_8px,_transparent_12px)]";
    default:
      return "before:!bg-gray-800";
  }
}

const standingsData = currentSeason.phases
  ? {
      positionLegend: currentSeason.metaInformation?.position_legend,
      secondaryPositionLegend: currentSeason.metaInformation?.secondary_position_legend,
      phase: findSeasonStandingPhase(currentSeason),
    }
  : undefined;

const getRowColor = (teamIndex: number, groupIndex: number): string | undefined => {
  const colors = getPositionLegendForGroup(standingsData?.positionLegend, groupIndex);
  const playoffPosition = colors ? colors[teamIndex] : undefined;
  return getRowPositionColorTailwindClass(playoffPosition?.color);
};

const getSecondaryRowColor = (teamIndex: number, groupIndex: number): string | undefined => {
  const colors = getPositionLegendForGroup(standingsData?.secondaryPositionLegend, groupIndex);
  const secondaryPlayoffPosition = colors ? colors[teamIndex] : undefined;
  return getRowPositionColorTailwindClass(secondaryPlayoffPosition?.color);
};

standingPhase.value = standingsData?.phase;
positionLegend.value = flattenPositionLegend(standingsData?.positionLegend);
secondaryPositionLegend.value = flattenPositionLegend(standingsData?.secondaryPositionLegend);
</script>

<template>
  <div v-if="standingPhase" class="bg-gray-900 p-2.5 sm:p-4 rounded-lg">
    <div
      v-for="(groupStanding, groupIndex) in standingPhase.groups.filter(g => g.hasStandings)"
      :key="groupStanding.id"
    >
      <div
        :class="[
          groupIndex === 0 ? 'justify-between' : 'justify-end',
          'flex text-gray-400 uppercase text-sm mb-2 items-end',
        ]"
      >
        <div class="flex flex-row gap-2">
          <p v-if="groupIndex === 0">{{ $t("tables.banner.title") }}</p>
        </div>

        <nuxt-link
          v-if="groupIndex === 0"
          :to="
            localePath({
              name: `region-Tables`,
              params: { region: regionParam },
            })
          "
          class="normal-case text-primary text-xs"
        >
          {{ $t("cards.stats.seeMore.link") }}
        </nuxt-link>
      </div>

      <div v-if="standingPhase.groups.length > 1" class="flex mb-2 justify-end">
        <span class="standing-group-title">
          <span class="standing-group-name">{{ groupStanding.name }}</span>
        </span>
      </div>

      <hr />

      <div class="header-table-standing">
        <span class="text-left rtl:text-right">{{ $t("tables.position.ab") }}</span>
        <span class="col-span-5 text-left rtl:text-right">{{ $t("tables.team.word") }}</span>
        <span class="text-center">{{ $t("tables.points.ab") }}</span>
        <span class="text-center">{{ $t("tables.gamesPlayed.ab") }}</span>
        <span class="text-center">{{ $t("tables.goalsDifference.ab") }}</span>
      </div>
      <div
        class="container-standing-rows"
        :class="groupIndex === 0 && standingPhase.groups.length > 1 ? 'mb-4' : 'mb-0'"
      >
        <div
          :class="['standing-row']"
          v-for="(standingTeam, teamIndex) in groupStanding.standings"
          :key="standingTeam.id"
        >
          <div
            :class="[
              'standing-rank',
              { 'secondary-standing': !!secondaryPositionLegend },
              getSecondaryRowColor(teamIndex, groupIndex),
            ]"
          >
            <span :class="['split-standing', getRowColor(teamIndex, groupIndex)]"></span>
            <p class="me-auto">{{ standingTeam.rank }}</p>
          </div>
          <div class="standing-team-container">
            <div class="logo-team-container">
              <NuxtImg
                v-if="standingTeam.team.logo?.url"
                :src="setAliasUrl(standingTeam.team.logo.url)"
                :alt="standingTeam.team.name"
                class="object-contain"
                loading="lazy"
                width="116"
                height="116"
                sizes="28px sm:32px md:44px"
              />
            </div>
            <nuxt-link
              :to="
                localePath({
                  name: 'teams-team-teamName',
                  params: {
                    team: standingTeam.team.id,
                    teamName: slugify(standingTeam.team.name),
                  },
                })
              "
              no-prefetch
            >
              {{ standingTeam.team.name }}
            </nuxt-link>
          </div>
          <span class="standing-table-item-data">{{ standingTeam.points }}</span>
          <span class="standing-table-item-data">{{ standingTeam.gameTotal }}</span>
          <span class="standing-table-item-data rounded-e-md">
            {{ standingTeam.goalPro - standingTeam.goalAgainst }}
          </span>
        </div>
      </div>

      <div
        v-if="groupIndex === standingPhase?.groups.filter(g => g.hasStandings).length - 1"
        class="flex flex-wrap items center justify-center gap-6 mt-6"
      >
        <standings-legend
          v-if="!!positionLegend && positionLegend.length"
          :legend="positionLegend"
          :grouped-colors="!!secondaryPositionLegend"
          :is-home-legend="!secondaryPositionLegend"
          ><span class="flex text-gray-400 uppercase text-sm">{{ contextSiteTitle }}</span>
        </standings-legend>
        <standings-legend
          v-if="!!secondaryPositionLegend && secondaryPositionLegend.length"
          :legend="secondaryPositionLegend"
          grouped-colors
        >
          <span class="flex text-gray-400 uppercase text-sm">KWC</span>
        </standings-legend>
      </div>
    </div>
  </div>
</template>

<style scoped>
.standing-group-title {
  @apply text-primary flex justify-center items-center text-[10px] gap-0.5;
}

.standing-group-name {
  @apply px-1.5 rounded border border-primary uppercase text-[10px] font-normal text-primary leading-[17px] ms-0.5;
}

.header-table-standing {
  @apply grid grid-cols-[8%_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] lg:grid-cols-9 text-gray-400 text-[10px] uppercase my-3;
}

.standing-row {
  @apply h-12 lg:h-[85px] grid grid-cols-[8%_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] lg:grid-cols-9 text-sm text-center font-bold mb-1;
}

.standing-rank {
  @apply h-full w-full flex items-center me-auto;
}

.secondary-standing {
  @apply before:min-w-[2px] before:sm:min-w-[3px] before:bg-gray-800 before:h-full before:rounded-sm;
}

span.split-standing {
  @apply h-full ms-0.5 me-auto;
}

span.split-standing::before {
  content: "";
  @apply min-w-[2px] sm:min-w-[3px] bg-gray-800 h-full rounded-sm me-auto block;
}

/*.container-standing-rows > div:nth-child(-n + 4) .standing-rank::before {
  @apply bg-emerald-600;
}*/

.standing-team-container {
  @apply col-span-5 text-left rtl:text-right flex justify-start gap-3 items-center bg-gray-800 rounded-s-md h-full pe-1;
}

.standing-team-container.world-cup-standing {
  @apply !col-span-6;
}

.logo-team-container {
  @apply h-8 w-8 lg:w-11 lg:h-11 flex justify-center items-center shrink-0;
}

.standing-table-item-data {
  @apply text-center bg-gray-800 h-full flex justify-center items-center;
}
</style>
